/**
 * Activity Log Service
 *
 * Feature: 002-activity-log
 * Task: T004
 * Description: Fire-and-forget activity logging service for tracking
 *              user actions on entities in the system.
 */
import firebase from '@/plugins/firebase';
import { database } from '@/plugins/firebase';
import store from '@/store';
import { CURRENT_SEASON_YEAR } from '@/store/seasonYear';
/**
 * Sanitizes an object by removing undefined values and non-serializable properties.
 * Uses JSON.parse(JSON.stringify()) pattern to ensure clean serialization.
 *
 * @param obj - Object to sanitize
 * @returns Sanitized object safe for Firestore storage
 */
function sanitizeData(obj) {
    if (obj === null) {
        return null;
    }
    try {
        return JSON.parse(JSON.stringify(obj));
    }
    catch {
        // If serialization fails, return null to avoid corrupting the log
        console.error('[ActivityLog] Failed to sanitize data:', obj);
        return null;
    }
}
/**
 * Gets the activity log collection name for the current year.
 *
 * @returns Collection name in format 'activity_log{YYYY}'
 */
function getCollectionName() {
    return `activity_log${CURRENT_SEASON_YEAR}`;
}
/**
 * Logs an activity entry to Firestore using fire-and-forget pattern.
 *
 * This function returns immediately after initiating the write operation.
 * Any errors are logged to console but do not throw or reject.
 *
 * @param action - Type of action performed (CREATE, UPDATE, DELETE)
 * @param entityType - Type of entity being modified
 * @param entityId - Unique identifier of the affected entity
 * @param previousValue - State before the change (null for CREATE)
 * @param newValue - State after the change (null for DELETE)
 */
export function logActivity(action, entityType, entityId, previousValue, newValue) {
    // Fire-and-forget: execute async operation but don't await
    void (async () => {
        try {
            // Get current user info from Vuex store
            const userId = store.getters['user/userId'] || 'unknown';
            // Get user display name from Firebase Auth if available
            const currentUser = firebase.auth().currentUser;
            const userName = currentUser?.displayName || currentUser?.email || 'Unknown User';
            // Build the log entry
            const logEntry = {
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                userId,
                userName,
                action,
                entityType,
                entityId,
                previousValue: sanitizeData(previousValue),
                newValue: sanitizeData(newValue),
            };
            // Write to Firestore
            const collectionName = getCollectionName();
            await database.collection(collectionName).add(logEntry);
        }
        catch (error) {
            // Silently handle errors - activity logging should never break the app
            console.error('[ActivityLog] Failed to log activity:', error);
        }
    })();
}
/**
 * Create entity ID for a reservation.
 */
export function createReservationEntityId(date, instructorId, hour) {
    return `${date}_${instructorId}_${hour}`;
}
/**
 * Create a reservation snapshot from cell data.
 */
export function createReservationSnapshot(date, instructorId, instructorName, cell) {
    return {
        date,
        instructorId,
        instructorName,
        hour: cell.hour,
        length: cell.length,
        service: cell.service,
        clients: cell.clients.map(c => ({ name: c.name, grade: c.grade })),
        phone: cell.phone,
        note: cell.note,
        price: cell.price,
    };
}
/**
 * Activity log service implementation.
 * Provides the scheduler integration interface while using the main logActivity function.
 */
class ActivityLogServiceImpl {
    async record(entry) {
        // Map scheduler actions to core action types
        const actionMap = {
            'RESERVATION_CREATED': 'CREATE',
            'RESERVATION_UPDATED': 'UPDATE',
            'RESERVATION_DELETED': 'DELETE',
            'RESERVATION_CANCELLED': 'DELETE',
            'RESERVATION_MOVED': 'UPDATE',
            'RESERVATION_PASTED': 'UPDATE',
        };
        const action = actionMap[entry.action] || 'UPDATE';
        logActivity(action, 'reservation', entry.entityId, entry.previousValue ? entry.previousValue : null, entry.newValue ? entry.newValue : null);
    }
    isEnabled() {
        return true; // Now enabled since Phase 1 is complete
    }
}
// Export singleton instance
export const activityLogService = new ActivityLogServiceImpl();
// Export class for testing
export { ActivityLogServiceImpl };
/**
 * Extract all reserved cells from a works document for comparison.
 */
export function extractReservationCells(workData) {
    const cells = [];
    if (!workData?.rows)
        return cells;
    const date = workData.date || '';
    const rows = workData.rows;
    for (const row of rows) {
        if (row.rowType !== 'instructor' || !row.instructorId)
            continue;
        for (const cell of row.cells || []) {
            if (cell.cellType !== 'reserved')
                continue;
            cells.push({
                date,
                instructorId: row.instructorId,
                hour: cell.hour,
                length: cell.length,
                service: cell.service,
                clients: cell.clients,
                phone: cell.phone,
                note: cell.note,
                price: cell.price,
                isPrepaid: cell.isPrepaid,
                voucher: cell.voucher,
                cellType: cell.cellType,
            });
        }
    }
    return cells;
}
/**
 * Create a unique ID for a reservation cell.
 */
export function createCellId(date, instructorId, hour) {
    return `${date}_${instructorId}_${hour}`;
}
/**
 * Find a matching cell by instructor ID and hour.
 */
export function findMatchingCell(cells, target) {
    return cells.find(c => c.instructorId === target.instructorId && c.hour === target.hour);
}
/**
 * Check if two cells have different data (excluding position).
 */
export function cellDataChanged(oldCell, newCell) {
    // Compare relevant fields
    if (oldCell.length !== newCell.length)
        return true;
    if (oldCell.service !== newCell.service)
        return true;
    if (oldCell.phone !== newCell.phone)
        return true;
    if (oldCell.note !== newCell.note)
        return true;
    if (oldCell.price !== newCell.price)
        return true;
    if (oldCell.isPrepaid !== newCell.isPrepaid)
        return true;
    // Compare clients
    if (JSON.stringify(oldCell.clients) !== JSON.stringify(newCell.clients))
        return true;
    // Compare voucher
    if (JSON.stringify(oldCell.voucher) !== JSON.stringify(newCell.voucher))
        return true;
    return false;
}
/**
 * Extract all available cells from a works document for comparison.
 */
export function extractAvailableCells(workData) {
    const cells = [];
    if (!workData?.rows)
        return cells;
    const date = workData.date || '';
    const rows = workData.rows;
    for (const row of rows) {
        if (row.rowType !== 'instructor' || !row.instructorId)
            continue;
        for (const cell of row.cells || []) {
            if (cell.cellType !== 'available')
                continue;
            cells.push({
                date,
                instructorId: row.instructorId,
                hour: cell.hour,
                length: cell.length,
                cellType: cell.cellType,
            });
        }
    }
    return cells;
}
/**
 * Find a matching available cell by instructor ID and hour.
 */
export function findMatchingAvailableCell(cells, target) {
    return cells.find(c => c.instructorId === target.instructorId && c.hour === target.hour);
}
/**
 * Check if two available cells have different data.
 */
export function availableCellDataChanged(oldCell, newCell) {
    // Compare relevant fields
    if (oldCell.length !== newCell.length)
        return true;
    return false;
}
